import { UAParser } from 'ua-parser-js'
import minutesOrHours from '@/lib/util/minutes-or-hours'
import Model from './Model'
import User from './User'
import KintellCard from './KintellCard'
import md from '@/lib/markdown'

export default class Booking extends Model {
  resource () {
    return 'bookings'
  }

  /**
   * Transform an object of data before it is used to fill this model.
   * Note: Make sure you're referring to data and not this as it wil break things.
   * @param data
   * @returns {{bookingTimeUtc}|*}
   */
  transform (data) {
    if (!data) {
      return data
    }
    if (data.advisor) {
      data.advisor = new User(data.advisor)
    }
    if (data.learner) {
      data.learner = new User(data.learner)
    }
    if (data.kintellCard) {
      data.kintellCard.user = data.advisor
      data.kintellCard = new KintellCard(data.kintellCard)
    }
    if (data.bookingTimeUtc) {
      data.bookingStartTimeUtc = this.$moment.utc(data.bookingTimeUtc)
      data.bookingEndTimeUtc = this.$moment.utc(data.bookingStartTimeUtc).add(data.duration, 'm')
    }
    if (data.createdAt) {
      data.createdAtUtc = this.$moment.utc(data.createdAt)
    }
    // flag it as real data
    data.isGhost = false
    return data
  }

  confirm (data) {
    return this.action('confirm', data)
  }

  cancel (data) {
    return this.action('cancel', data)
  }

  suggestTimes (data) {
    return this.action('suggest-times', data)
  }

  async fetchChatToken () {
    return (await this.$http.$post(this.resourceUrl() + '/chat-token')).data
  }

  async fetchVideoToken () {
    return (await this.$http.$post(this.resourceUrl() + '/video-token')).data
  }

  time () {
    return Booking.custom(this, 'time').first()
  }

  complete () {
    // loadBookingForSlug doesn't do anything if
    // it's already loaded but out of date
    this.status = 'completed'

    return this.action('complete')
  }

  get link () {
    return `/my-kintell/bookings/${this.id}`
  }

  /**
   * Compares the current UTC time to the booking start time UTC.
   * @returns {boolean}
   */
  get hasStarted () {
    return this.$moment.utc().isSameOrAfter(this.bookingStartTimeUtc, 'minute')
  }

  get isStarting () {
    return this.$moment.utc().isSame(this.bookingStartTimeUtc, 'minute')
  }

  get canJoinBooking () {
    // user can join 60 minutes before the booking is supposed to start
    const joinBookingTime = this.$moment.utc(this.bookingStartTimeUtc).subtract('60', 'minutes')
    return this.$moment.utc().isSameOrAfter(joinBookingTime, 'minute')
  }

  get canJoinCall () {
    // user can actually connect to the room 60min before the start time
    const joinBookingTime = this.$moment.utc(this.bookingStartTimeUtc).subtract('60', 'minutes')
    return this.$moment.utc().isSameOrAfter(joinBookingTime, 'minute')
  }

  get hasEnded () {
    return this.$moment.utc().isSameOrAfter(this.bookingEndTimeUtc, 'minute')
  }

  /**
   * A fresh booking in this instance will be a booking that was created in the last 5 minutes. This is
   * useful for when we want to avoid making extra requests when it's not needed (i.e chat tokens)
   * @returns {boolean}
   */
  get isFreshBooking () {
    const minutesSinceMade = this.$moment.utc().diff(this.createdAtUtc, 'minutes')
    return minutesSinceMade < 5
  }

  /**
   * Checks the time between the current UTC time to the booking start time UTC.
   * @returns {string}
   */
  get startsInHumanTime () {
    if (this.isStarting) {
      return 'now'
    }
    const diffString = minutesOrHours(
      Math.abs(this.$moment.utc().diff(this.bookingStartTimeUtc, 'minutes') + 1),
      {
        // at what interval to go from hours to minutes
        minutesToHoursThreshold: 240
      }
    )
    if (this.hasStarted) {
      return diffString + ' ago'
    }
    return 'in ' + diffString
  }

  get minutesUntilBookingStart () {
    return this.$moment.utc().diff(this.bookingStartTimeUtc, 'minutes')
  }

  get endsInTime () {
    return this.$moment.utc().to(this.bookingEndTimeUtc)
  }

  get humanStatus () {
    if (this.status === 'pending-approval') {
      return 'Awaiting Response'
    } else if (this.status === 'suggested-times') {
      return 'Awaiting Response'
    }
  }

  get isConfirmed () {
    return this.status === 'confirmed' || this.status === 'completed'
  }

  get isCompleted () {
    return this.status === 'completed'
  }

  get joinBookingLink () {
    return '/my-kintell/bookings/' + this.id + '/join'
  }

  get joinBookingInApp () {
    const ua = new UAParser()
    const jwtToken = window.localStorage.getItem('auth._token.local')
    const url = window.location.origin
    const browser = ua.getBrowser().name
    const apiURL = process.env.API_URL

    const queryStringForLink = require('query-string')
    const tokenAndBookingId = { token: jwtToken, bookingId: this.id, browser, api: apiURL }
    const link = url + '/data?' + queryStringForLink.stringify(tokenAndBookingId)

    const dLinkDomain = 'https://kintellapp.page.link' // Firebase dynamic link hosting url
    const isi = 1419104805 // App's App Store ID, used to send users to the App Store when the app isn't installed
    const ibi = 'com.praiject.kintellapp' // Apple app bundle id
    const utmSource = 'Download Kintell App' // Browser page title if app is not installed
    // utm_medium - Browser image url - currently set to kintell logo of App store
    const utmMedium = 'https://is3-ssl.mzstatic.com/image/thumb/Purple118/v4/e1/49/e6/e149e64c-8348-9be5-998f-49a66e3420a9/AppIcon-1x_U007emarketing-0-0-GLES2_U002c0-512MB-sRGB-0-0-0-85-220-0-0-0-7.png/690x0w.png'
    const utmCampaign = 'You have to download Kintell App for the booking on iOS' // Browser detail message if app is not installed
    const ofl = url + '/my-kintell/bookings/' + this.id + '/join' // The link to open on platforms beside Android and iOS. This is useful to specify a different behavior on desktop, like displaying a full web page

    const st = 'Open to download our Companion App'
    const sd = 'This app is used for all video sessions on iOS devices, to ensure the best video quality possible.'

    const queryStringFordLink = require('query-string')
    const dLinkData = { link, isi, ibi, utm_campaign: utmCampaign, utm_medium: utmMedium, utm_source: utmSource, ofl, st, sd }
    const dLink = dLinkDomain + '?' + queryStringFordLink.stringify(dLinkData)

    return dLink
  }

  get topicMarkdown () {
    const topic = this.topic
    // @todo Find out the performance cost of this and see if we should have this async
    return topic && md.render(topic)
  }

  get confirmBookingLink () {
    return '/my-kintell/bookings/' + this.id + '/confirm'
  }

  get suggestBookingLink () {
    return '/my-kintell/bookings/' + this.id + '/suggest-times'
  }

  get rescheduleBookingLink () {
    return '/my-kintell/bookings/' + this.id + '/reschedule'
  }

  get respondInitiatedBookingRequestLink () {
    return this.kintellCard.link + '/respond?booking=' + this.id
  }

  get isArchived () {
    return [
      'provisional-request-expired',
      'completed',
      'cancelled-confirmed',
      'cancelled-provisional',
      'expired-both-no-show',
      'expired-advisor-no-show',
      'expired-learner-no-show',
    ].includes(this.status)
  }

  get isPendingApproval () {
    return this.status === 'pending-approval'
  }

  get wasRescheduleByAdvisor () {
    return this.rescheduledBy === this.advisorId
  }

  get wasRescheduledByLearner () {
    return this.rescheduledBy === this.learnerId
  }

  get wasCreatedByAdvisor () {
    return this.advisorId === this.createdBy
  }

  get isAdvisorInitiatedAndPendingApproval () {
    return this.wasCreatedByAdvisor && this.isPendingApproval
  }

  get learnerIsPlaceholder () {
    return this.learner.isPlaceholder
  }
}
